import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const STORAGE_PREFIX = 'beam:scope-form:';

const emptyDraft = () => ({
  scopes: {},
  elements: {},
  modifiers: {},
  notes: {},
  typicalSheetRange: null,
  chosenPresets: {}
});

const draftFields = d => ({
  scopes: d?.scopes || {},
  elements: d?.elements || {},
  modifiers: d?.modifiers || {},
  notes: d?.notes || {},
  typicalSheetRange: d?.typicalSheetRange ?? null,
  chosenPresets: d?.chosenPresets || {}
});

const readStoredDraft = storageKey => {
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return {
      ...draftFields(parsed),
      serverRevision: parsed.serverRevision ?? null
    };
  } catch {
    // SecurityError when site data is blocked, or invalid JSON — treat as no draft.
    return null;
  }
};

const clearStoredDraft = storageKey => {
  if (!storageKey) return;
  try {
    window.localStorage.removeItem(storageKey);
  } catch {
    // ignore
  }
};

const captureModifierAnswer = (into, m) => {
  const opts = m?.selected_options || [];
  const text = m?.text_value || '';
  if (opts.length === 0 && text.length === 0) return;
  into[m.id || m.modifier_id] = {
    selected_options: opts,
    text_value: text
  };
};

// Backend returns previous selections nested inside `trades_in_scope`.
// Flatten to the draft shape the form uses.
export const hydrateFromTradesInScope = tradesInScope => {
  if (!Array.isArray(tradesInScope) || tradesInScope.length === 0) return null;
  const scopes = {};
  const elements = {};
  const modifiers = {};
  const notes = {};
  tradesInScope.forEach(trade => {
    if (trade?.notes && String(trade.notes).trim()) {
      notes[trade.trade_id] = trade.notes;
    }
    (trade?.trade_modifiers || []).forEach(m => captureModifierAnswer(modifiers, m));
    (trade?.sections || []).forEach(section => {
      (section?.assigned_scopes || []).forEach(scope => {
        if (!scope?.id) return;
        scopes[scope.id] = true;
        (scope.elements || []).forEach(el => {
          if (el?.selected && el.id) elements[el.id] = true;
        });
        (scope.modifiers || []).forEach(m => captureModifierAnswer(modifiers, m));
      });
    });
  });
  return { scopes, elements, modifiers, notes };
};

/** True when the draft has any customer progress worth keeping (incl. TAT-only). */
export const isNonEmptyDraft = d => {
  if (!d) return false;
  if (Object.keys(d.scopes || {}).length > 0) return true;
  if (Object.keys(d.elements || {}).length > 0) return true;
  if (Object.keys(d.modifiers || {}).length > 0) return true;
  if (Object.values(d.notes || {}).some(v => (v || '').trim())) return true;
  // Index 0 is a valid typical-sheet chip — must use != null, not truthiness.
  if (d.typicalSheetRange != null) return true;
  if (Object.keys(d.chosenPresets || {}).some(k => d.chosenPresets[k] != null && d.chosenPresets[k] !== '')) {
    return true;
  }
  return false;
};

/**
 * Prefer server selections when present unless local draft was saved against the same
 * serverRevision (continued edits). preferServer (e.g. submitted/read-only) always wins.
 */
export const resolveDraft = ({ storageKey, tradesInScope, serverRevision, preferServer }) => {
  const server = hydrateFromTradesInScope(tradesInScope);
  const local = storageKey ? readStoredDraft(storageKey) : null;

  if (preferServer) {
    clearStoredDraft(storageKey);
    return draftFields(server || emptyDraft());
  }

  if (isNonEmptyDraft(server)) {
    const localMatchesServer =
      isNonEmptyDraft(local) &&
      serverRevision != null &&
      local.serverRevision != null &&
      String(local.serverRevision) === String(serverRevision);
    // Same revision: keep local edits including chosenPresets / typicalSheetRange.
    if (localMatchesServer) return draftFields(local);
    // Fresh server selections: preset clicks are not carried forward from a prior version,
    // but typical sheet range is local-only (not in trades_in_scope) — keep it.
    return draftFields({
      ...server,
      chosenPresets: {},
      typicalSheetRange: local?.typicalSheetRange ?? null
    });
  }

  if (isNonEmptyDraft(local)) return draftFields(local);
  return emptyDraft();
};

const useFormDraft = ({
  token,
  tradesInScope,
  persist = true,
  serverRevision = null,
  preferServer = false
}) => {
  const storageKey = persist && token ? STORAGE_PREFIX + token : null;
  const initial = useMemo(
    () =>
      resolveDraft({
        storageKey,
        tradesInScope,
        serverRevision,
        preferServer
      }),
    // Payload/revision arrive async — effect below re-resolves.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [storageKey]
  );

  const [draft, setDraft] = useState(initial);
  const [savedAt, setSavedAt] = useState(null);
  const skipNextWrite = useRef(false);
  const lastResolvedKey = useRef(`${storageKey}|${serverRevision}|${preferServer}|init`);

  // Re-resolve when server selections / revision arrive or form becomes read-only.
  useEffect(() => {
    const key = `${storageKey}|${serverRevision}|${preferServer}|${Array.isArray(tradesInScope) ? tradesInScope.length : 'x'}`;
    if (key === lastResolvedKey.current) return;
    lastResolvedKey.current = key;
    const next = resolveDraft({ storageKey, tradesInScope, serverRevision, preferServer });
    skipNextWrite.current = preferServer;
    setDraft(next);
  }, [storageKey, tradesInScope, serverRevision, preferServer]);

  useEffect(() => {
    if (!storageKey || preferServer) return;
    if (skipNextWrite.current) {
      skipNextWrite.current = false;
      return;
    }
    try {
      window.localStorage.setItem(
        storageKey,
        JSON.stringify({
          ...draft,
          serverRevision: serverRevision ?? null
        })
      );
      setSavedAt(Date.now());
    } catch {
      // storage full / disabled — silently continue
    }
  }, [draft, storageKey, serverRevision, preferServer]);

  const toggleScope = useCallback(
    (scopeId, elementIds = [], modifierIds = []) =>
      setDraft(d => {
        const scopes = { ...d.scopes };
        const elements = { ...d.elements };
        const modifiers = { ...d.modifiers };
        if (scopes[scopeId]) {
          delete scopes[scopeId];
          elementIds.forEach(id => delete elements[id]);
          modifierIds.forEach(id => delete modifiers[id]);
        } else {
          scopes[scopeId] = true;
          elementIds.forEach(id => {
            elements[id] = true;
          });
        }
        return { ...d, scopes, elements, modifiers };
      }),
    []
  );

  const toggleElement = useCallback(
    elementId =>
      setDraft(d => {
        const elements = { ...d.elements };
        if (elements[elementId]) delete elements[elementId];
        else elements[elementId] = true;
        return { ...d, elements };
      }),
    []
  );

  const setModifierAnswer = useCallback(
    (modifierId, patch) =>
      setDraft(d => {
        const prev = d.modifiers[modifierId] || { selected_options: [], text_value: '' };
        return {
          ...d,
          modifiers: {
            ...d.modifiers,
            [modifierId]: { ...prev, ...patch }
          }
        };
      }),
    []
  );

  const setNote = useCallback(
    (tradeId, value) =>
      setDraft(d => ({ ...d, notes: { ...d.notes, [tradeId]: value } })),
    []
  );

  const clearTrade = useCallback(
    trade => {
      const scopeIds = (trade.scopes || []).map(s => s.scope_id);
      const elementIds = (trade.scopes || []).flatMap(s => (s.elements || []).map(e => e.id));
      const modifierIds = [
        ...(trade.trade_modifiers || []).map(m => m.id),
        ...(trade.scopes || []).flatMap(s => (s.modifiers || []).map(m => m.id))
      ];
      setDraft(d => {
        const scopes = { ...d.scopes };
        scopeIds.forEach(id => delete scopes[id]);
        const elements = { ...d.elements };
        elementIds.forEach(id => delete elements[id]);
        const modifiers = { ...d.modifiers };
        modifierIds.forEach(id => delete modifiers[id]);
        const notes = { ...d.notes };
        delete notes[trade.trade_id];
        const chosenPresets = { ...(d.chosenPresets || {}) };
        delete chosenPresets[trade.trade_id];
        return {
          scopes,
          elements,
          modifiers,
          notes,
          typicalSheetRange: d.typicalSheetRange ?? null,
          chosenPresets
        };
      });
    },
    []
  );

  const applyPreset = useCallback((nextDraft, meta) => {
    setDraft(d => {
      const base = typeof nextDraft === 'function' ? nextDraft(d) : nextDraft;
      if (meta?.tradeId && meta?.presetId) {
        return {
          ...base,
          chosenPresets: {
            ...(base.chosenPresets || d.chosenPresets || {}),
            [meta.tradeId]: String(meta.presetId)
          }
        };
      }
      return { ...base, chosenPresets: base.chosenPresets || d.chosenPresets || {} };
    });
  }, []);

  const setTypicalSheetRange = useCallback(idx => {
    setDraft(d => ({ ...d, typicalSheetRange: idx }));
  }, []);

  const purge = useCallback(() => {
    skipNextWrite.current = true;
    setDraft(emptyDraft());
    setSavedAt(null);
    clearStoredDraft(storageKey);
  }, [storageKey]);

  return {
    draft,
    savedAt,
    toggleScope,
    toggleElement,
    setModifierAnswer,
    setNote,
    clearTrade,
    applyPreset,
    setTypicalSheetRange,
    purge
  };
};

export default useFormDraft;
